import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_OG_IMAGE_PATH,
  DEFAULT_KEYWORDS,
  TWITTER_HANDLE,
} from '../config/siteSeo';
import type { SeoMeta } from '../types/seo';

function absoluteUrl(pathOrUrl?: string) {
  if (!pathOrUrl) return `${SITE_URL}${DEFAULT_OG_IMAGE_PATH}`;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${SITE_URL}${path}`;
}

function normalizePath(path?: string) {
  if (!path) return '/';
  const p = path.startsWith('/') ? path : `/${path}`;
  return p.replace(/\/{2,}/g, '/');
}

export interface SeoHeadProps extends SeoMeta {
  path: string;
  ogType?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
}

/**
 * Per-route SEO: title template, meta description, keywords, canonical, Open Graph, Twitter.
 */
function SeoHead({
  title,
  description,
  path,
  keywords,
  ogType = 'website',
  image,
  imageAlt,
  noindex = false,
  articlePublishedTime,
  articleModifiedTime,
}: SeoHeadProps) {
  const canonicalPath = normalizePath(path);
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const pageTitle = `${title} | ${SITE_NAME}`;
  const desc = String(description || '').trim().slice(0, 320);
  const kw = (keywords != null ? keywords : DEFAULT_KEYWORDS) || '';
  const ogImage = absoluteUrl(image || DEFAULT_OG_IMAGE_PATH);

  return (
    <Helmet>
      <html lang="en" />
      <title>{pageTitle}</title>
      <meta name="description" content={desc} />
      {kw ? <meta name="keywords" content={kw} /> : null}
      <meta name="author" content={SITE_NAME} />
      <link rel="canonical" href={canonicalUrl} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
      )}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={imageAlt || title} />
      <meta property="og:locale" content="en_IN" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {ogType === 'article' && articlePublishedTime ? (
        <meta property="article:published_time" content={articlePublishedTime} />
      ) : null}
      {ogType === 'article' && articleModifiedTime ? (
        <meta property="article:modified_time" content={articleModifiedTime} />
      ) : null}
    </Helmet>
  );
}

export default SeoHead;
